import { ORG_CONTEXT_REJECTION_CODES } from '@cmoh/shared'
import { z } from 'zod'

/**
 * Per-tab organization store. Each browser tab pins the organization it shows in sessionStorage
 * and sends it on every API request, so tabs on different organizations never cross-write.
 * Resolution at load: `?org=` → sessionStorage → the session's active organization.
 */

const TAB_ORGANIZATION_STORAGE_KEY = 'referent.tab-organization.v1'
const REJECTED_TAB_ORGANIZATION_STORAGE_KEY = 'referent.tab-organization.rejected.v1'
const ENTRY_PATH = '/'
const NEW_TAB_TARGET = '_blank'
const NEW_TAB_FEATURES = 'noopener,noreferrer'
export const TAB_ORGANIZATION_QUERY_PARAM = 'org'

const organizationIdSchema = z.guid()
const rejectionCodes: ReadonlySet<string> = new Set(ORG_CONTEXT_REJECTION_CODES)

let tabOrganizationId: string | null = null
let isRecovering = false
let isPinningSuspended = false

function isOrganizationId(value: string | null | undefined): value is string {
  return organizationIdSchema.safeParse(value).success
}

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    return
  }
}

function removeStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    return
  }
}

export function initializeTabOrganization(): void {
  const url = new URL(window.location.href)
  const requested = url.searchParams.get(TAB_ORGANIZATION_QUERY_PARAM)
  if (requested !== null) {
    url.searchParams.delete(TAB_ORGANIZATION_QUERY_PARAM)
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }
  if (isOrganizationId(requested)) {
    setTabOrganizationId(requested)
    return
  }
  const stored = readStorage(TAB_ORGANIZATION_STORAGE_KEY)
  tabOrganizationId = isOrganizationId(stored) ? stored : null
}

export function getTabOrganizationId(): string | null {
  return tabOrganizationId
}

export function setTabOrganizationId(organizationId: string): void {
  tabOrganizationId = organizationId
  writeStorage(TAB_ORGANIZATION_STORAGE_KEY, organizationId)
  removeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY)
}

/**
 * `untilReload` blocks re-pinning until the page unloads, so a session fetch still in flight from
 * the previous identity cannot pin its org back before a full-page redirect.
 */
export function clearTabOrganization({
  untilReload = false,
}: {
  untilReload?: boolean
} = {}): void {
  isPinningSuspended = untilReload
  tabOrganizationId = null
  removeStorage(TAB_ORGANIZATION_STORAGE_KEY)
  removeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY)
}

export function pinTabOrganization(sessionOrganizationId: string | null | undefined): void {
  if (isPinningSuspended || tabOrganizationId !== null) return
  if (!isOrganizationId(sessionOrganizationId)) return
  if (readStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY) === sessionOrganizationId) return
  tabOrganizationId = sessionOrganizationId
  writeStorage(TAB_ORGANIZATION_STORAGE_KEY, sessionOrganizationId)
}

export function recoverFromRejectedTabOrganization(
  sentOrganizationId: string,
  errorCode: string | undefined,
): void {
  if (isRecovering || sentOrganizationId !== tabOrganizationId) return
  if (errorCode === undefined || !rejectionCodes.has(errorCode)) return
  isRecovering = true
  tabOrganizationId = null
  removeStorage(TAB_ORGANIZATION_STORAGE_KEY)
  writeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY, sentOrganizationId)
  window.location.reload()
}

export function openOrganizationInNewTab(organizationId: string): void {
  const url = new URL(ENTRY_PATH, window.location.origin)
  url.searchParams.set(TAB_ORGANIZATION_QUERY_PARAM, organizationId)
  window.open(url.toString(), NEW_TAB_TARGET, NEW_TAB_FEATURES)
}
